import { Badge, Button, Card, Code, Group, Pagination, Table, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { api } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';

interface AuditItem { id: number; occurredAt: string; actorName: string | null; action: string; objectType: string; objectId: string | null; details: Record<string, unknown> }

const COLOR: Record<string, string> = { auth: 'red', admin: 'violet', role: 'grape', course: 'blue', version: 'indigo', lesson: 'cyan', question: 'cyan', assignment: 'orange', quiz: 'teal', report: 'yellow', results: 'yellow', user: 'pink', retention: 'gray', forbidden: 'red', mail: 'red', file: 'gray' };
const PAGE = 30;

export function AuditPage() {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [object, setObject] = useState('');
  const [range, setRange] = useState<[string | null, string | null]>([null, null]);
  const [page, setPage] = useState(1);
  const [a] = useDebouncedValue(action, 300);
  const [ac] = useDebouncedValue(actor, 300);
  const [o] = useDebouncedValue(object, 300);
  const [type, id] = o.includes('#') ? o.split('#') : [o, ''];
  const qs = new URLSearchParams(Object.entries({
    action: a, actor: ac, objectType: type.trim(), objectId: id.trim(),
    from: range[0] ? dayjs(range[0]).startOf('day').toISOString() : '', to: range[1] ? dayjs(range[1]).endOf('day').toISOString() : '',
    limit: String(PAGE), offset: String((page - 1) * PAGE),
  }).filter(([, v]) => v)).toString();
  const q = useQuery({ queryKey: ['audit', qs], queryFn: () => api.get<{ total: number; items: AuditItem[] }>(`/admin/audit?${qs}`) });
  return (
    <>
      <PageHeader title="Журнал аудита" subtitle="Только добавление: записи нельзя изменить или удалить даже из базы. Поиск по действию, автору, объекту и дате." />
      <Card withBorder radius="lg" padding="md" mb="md">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput label="Действие" placeholder="например assignment или auth.denied" value={action} onChange={(e) => { setAction(e.currentTarget.value); setPage(1); }} w={230} />
          <TextInput label="Автор" placeholder="ФИО или email" value={actor} onChange={(e) => { setActor(e.currentTarget.value); setPage(1); }} w={200} />
          <TextInput label="Объект" placeholder="assignment#12" value={object} onChange={(e) => { setObject(e.currentTarget.value); setPage(1); }} w={170} />
          <DatePickerInput type="range" label="Дата" placeholder="Любая" clearable w={230} valueFormat="D MMM YYYY" value={range} onChange={(v) => { setRange(v as [string | null, string | null]); setPage(1); }} />
          <Button variant="subtle" onClick={() => { setAction(''); setActor(''); setObject(''); setRange([null, null]); setPage(1); }}>Сбросить</Button>
        </Group>
      </Card>
      <QueryState loading={q.isLoading} error={q.error}>
        {() => (
          <>
            <Card withBorder radius="lg" p={0}>
              <Table.ScrollContainer minWidth={860}>
                <Table verticalSpacing="xs" horizontalSpacing="md" striped>
                  <Table.Thead><Table.Tr><Table.Th>Время</Table.Th><Table.Th>Кто</Table.Th><Table.Th>Действие</Table.Th><Table.Th>Объект</Table.Th><Table.Th>Детали</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {q.data!.items.map((i) => (
                      <Table.Tr key={i.id}>
                        <Table.Td><Text size="xs" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(i.occurredAt)}</Text></Table.Td>
                        <Table.Td><Text size="sm">{i.actorName ?? 'система'}</Text></Table.Td>
                        <Table.Td><Badge variant="light" color={COLOR[i.action.split('.')[0]] ?? 'gray'} size="sm">{i.action}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{i.objectType}{i.objectId ? ` #${i.objectId.slice(0, 24)}` : ''}</Text></Table.Td>
                        <Table.Td><Code style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(i.details)}</Code></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
            <Group justify="space-between" mt="md">
              <Text size="xs" c="dimmed">Всего: {q.data!.total}</Text>
              <Pagination total={Math.max(1, Math.ceil(q.data!.total / PAGE))} value={page} onChange={setPage} size="sm" />
            </Group>
          </>
        )}
      </QueryState>
    </>
  );
}
